/**
 * Première connexion + Mot de passe oublié — version sécurisée.
 *
 * Flow `reset=false` (première activation) :
 *   1. matricule + date naissance → reset_token
 *   2. mot de passe + QUESTION SECRÈTE + RÉPONSE (obligatoires) → access_token
 *
 * Flow `reset=true` (mot de passe oublié) :
 *   - Si le compte a déjà une question secrète :
 *     1. matricule → renvoie la question (resetInit)
 *     2. date naissance + réponse → reset_token (resetVerify)
 *     3. nouveau mot de passe → access_token
 *   - Si compte existant SANS question (anciens utilisateurs) :
 *     même flow que première activation, mais marqué « compte_non_securise »
 *     pour forcer la création de la question dès maintenant.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { PortailLayout } from '../PortailLayout';
import { portailApi } from '../services/portailApi';

interface Props {
  reset?: boolean;
}

type Step = 'identif' | 'question' | 'setpass';

export default function PremiereConnexion({ reset = false }: Props) {
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('identif');
  const [matricule, setMatricule] = useState('');
  const [dateNaissance, setDateNaissance] = useState('');
  const [questionSecurite, setQuestionSecurite] = useState('');
  const [reponseSecurite, setReponseSecurite] = useState('');

  // Pour reset sécurisé : la question existante de l'étudiant
  const [questionExistante, setQuestionExistante] = useState<string | null>(null);

  const [resetToken, setResetToken] = useState('');
  const [etudiantInfo, setEtudiantInfo] = useState<{ nom: string; prenoms: string } | null>(null);
  const [compteNonSecurise, setCompteNonSecurise] = useState(false);

  const [password, setPassword] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pre-fill depuis findMatricule
  useEffect(() => {
    const prefillMat = sessionStorage.getItem('portail_prefill_matricule');
    const prefillDate = sessionStorage.getItem('portail_prefill_date');
    if (prefillMat) setMatricule(prefillMat);
    if (prefillDate) setDateNaissance(prefillDate);
  }, []);

  // ──────────────────────────────────────────────────────────────
  // ÉTAPE 1 — Identification
  // ──────────────────────────────────────────────────────────────

  const onMatriculeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const mat = matricule.trim().toUpperCase();
    if (!mat) {
      setError('Matricule requis');
      return;
    }
    setLoading(true);
    try {
      if (reset) {
        // Tente le flow sécurisé d'abord (compte avec question)
        try {
          const r = await portailApi.resetInit({ matricule: mat });
          setQuestionExistante(r.question_securite);
          // Demande date + réponse à l'étape suivante
          setStep('question');
          sessionStorage.removeItem('portail_prefill_matricule');
          sessionStorage.removeItem('portail_prefill_date');
          return;
        } catch (e: any) {
          // 404 = compte sans question (mode dégradé) ou compte inexistant
          // On bascule sur le flow legacy
        }
      }

      // Flow legacy : matricule + date naissance ensemble
      if (!dateNaissance) {
        setError('Date de naissance requise');
        setLoading(false);
        return;
      }
      const res = await portailApi.firstLogin({ matricule: mat, date_naissance: dateNaissance });
      if (!reset && res.has_password && !res.requires_question_securite) {
        setError("Un mot de passe existe déjà pour ce matricule. Connectez-vous directement.");
        return;
      }
      setResetToken(res.reset_token);
      setEtudiantInfo({ nom: res.nom, prenoms: res.prenoms });
      setCompteNonSecurise(Boolean((res as any).compte_non_securise));
      setStep('setpass');
      sessionStorage.removeItem('portail_prefill_matricule');
      sessionStorage.removeItem('portail_prefill_date');
    } catch (e: any) {
      // Si 403 requires_secure_reset → suggérer de cliquer sur "Mot de passe oublié"
      if (e?.data?.requires_secure_reset) {
        setError("Ce compte est sécurisé. Utilisez « Mot de passe oublié » au lieu de « Première connexion ».");
      } else {
        setError(e.message || 'Erreur');
      }
    } finally {
      setLoading(false);
    }
  };

  // ──────────────────────────────────────────────────────────────
  // ÉTAPE 2 — Date + Réponse à la question (uniquement reset sécurisé)
  // ──────────────────────────────────────────────────────────────

  const onQuestionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!dateNaissance || !reponseSecurite.trim()) {
      setError('Date de naissance et réponse requises');
      return;
    }
    setLoading(true);
    try {
      const r = await portailApi.resetVerify({
        matricule: matricule.trim().toUpperCase(),
        date_naissance: dateNaissance,
        reponse_securite: reponseSecurite,
      });
      setResetToken(r.reset_token);
      setEtudiantInfo({ nom: r.nom, prenoms: r.prenoms });
      setStep('setpass');
    } catch (e: any) {
      setError(e.message || 'Identifiants incorrects');
    } finally {
      setLoading(false);
    }
  };

  // ──────────────────────────────────────────────────────────────
  // ÉTAPE 3 — Nouveau mot de passe (+ Q/R si création/dégradé)
  // ──────────────────────────────────────────────────────────────

  const requireQR = !reset || compteNonSecurise || !questionExistante;

  const onPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError('Mot de passe ≥ 8 caractères');
      return;
    }
    if (/^\d+$/.test(password)) {
      setError('Le mot de passe ne peut pas être uniquement numérique');
      return;
    }
    if (password !== confirmPwd) {
      setError("Les mots de passe ne correspondent pas");
      return;
    }
    if (requireQR) {
      if (questionSecurite.trim().length < 5) {
        setError("Question ≥ 5 caractères");
        return;
      }
      if (reponseSecurite.trim().length < 2) {
        setError("Réponse ≥ 2 caractères");
        return;
      }
    }
    setLoading(true);
    try {
      const payload: any = { reset_token: resetToken, password };
      if (requireQR) {
        payload.question_securite = questionSecurite.trim();
        payload.reponse_securite = reponseSecurite.trim();
      }
      const r = await portailApi.setPassword(payload);
      portailApi.setToken(r.access_token);
      portailApi.setEtudiant({ matricule: r.matricule, nom: r.nom, prenoms: r.prenoms });
      navigate('/portail/accueil', { replace: true });
    } catch (e: any) {
      setError(e.message || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  // ──────────────────────────────────────────────────────────────
  // Rendu
  // ──────────────────────────────────────────────────────────────

  return (
    <PortailLayout showHeader title={reset ? 'Mot de passe oublié' : 'Première connexion'}>
      <div className="space-y-4 mt-4">
        {step === 'identif' && (
          <>
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 text-sm text-blue-800 dark:text-blue-200">
              {reset
                ? "Saisissez votre matricule. Si votre compte est protégé par une question secrète, nous vous la poserons à l'étape suivante."
                : "Saisissez votre matricule et votre date de naissance pour activer votre compte. Vous devrez créer un mot de passe et une question secrète."}
            </div>
            <form onSubmit={onMatriculeSubmit} className="bg-white dark:bg-gray-800 rounded-2xl shadow p-5 space-y-3 border border-gray-100 dark:border-gray-700">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Matricule *</label>
                <input
                  type="text" value={matricule}
                  onChange={(e) => setMatricule(e.target.value)}
                  required autoCapitalize="characters"
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                />
              </div>
              {!reset && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date de naissance *</label>
                  <input
                    type="date" value={dateNaissance}
                    onChange={(e) => setDateNaissance(e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                </div>
              )}
              {error && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">{error}</div>
              )}
              <button
                type="submit" disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg disabled:opacity-50"
              >
                {loading ? 'Vérification...' : 'Continuer'}
              </button>
            </form>
          </>
        )}

        {step === 'question' && (
          <>
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 text-sm text-amber-800 dark:text-amber-200">
              🔐 Pour vérifier votre identité, répondez à la question secrète que vous aviez choisie + votre date de naissance.
            </div>
            <form onSubmit={onQuestionSubmit} className="bg-white dark:bg-gray-800 rounded-2xl shadow p-5 space-y-3 border border-gray-100 dark:border-gray-700">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date de naissance *</label>
                <input
                  type="date" value={dateNaissance}
                  onChange={(e) => setDateNaissance(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Question secrète
                </label>
                <div className="px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded text-sm text-gray-700 dark:text-gray-300 italic mb-2">
                  « {questionExistante} »
                </div>
                <input
                  type="text" value={reponseSecurite}
                  onChange={(e) => setReponseSecurite(e.target.value)}
                  required autoFocus
                  placeholder="Votre réponse"
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                />
                <div className="text-[11px] text-gray-500 mt-1">
                  La casse et les espaces ne comptent pas.
                </div>
              </div>
              {error && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">{error}</div>
              )}
              <button
                type="submit" disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg disabled:opacity-50"
              >
                {loading ? 'Vérification...' : 'Vérifier'}
              </button>
              <button
                type="button" onClick={() => setStep('identif')}
                className="w-full text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 mt-2"
              >
                ← Modifier mon matricule
              </button>
            </form>
          </>
        )}

        {step === 'setpass' && etudiantInfo && (
          <>
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 text-sm">
              <div className="text-green-800 dark:text-green-200">
                Identité confirmée : <strong>{etudiantInfo.nom} {etudiantInfo.prenoms}</strong>
              </div>
              <div className="text-green-700 dark:text-green-300 mt-1 text-xs">
                {reset && !compteNonSecurise
                  ? "Choisissez votre nouveau mot de passe."
                  : compteNonSecurise
                  ? "Votre compte n'est pas encore sécurisé. Veuillez créer une question de sécurité maintenant."
                  : "Créez votre mot de passe et votre question de sécurité."}
              </div>
            </div>

            <form onSubmit={onPasswordSubmit} className="bg-white dark:bg-gray-800 rounded-2xl shadow p-5 space-y-3 border border-gray-100 dark:border-gray-700">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Mot de passe (≥ 8 caractères)</label>
                <input
                  type="password" value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required minLength={6} autoFocus
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Confirmer</label>
                <input
                  type="password" value={confirmPwd}
                  onChange={(e) => setConfirmPwd(e.target.value)}
                  required minLength={6}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                />
              </div>

              {requireQR && (
                <>
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      🔐 Question de sécurité
                    </div>
                    <div className="text-[11px] text-gray-500 dark:text-gray-400 mb-3 leading-relaxed">
                      Choisissez une question dont seul vous connaissez la réponse. Elle sera demandée à chaque réinitialisation. <strong>Pas la date de naissance ni le nom de votre option</strong> (trop facile à deviner).
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Question *</label>
                      <input
                        type="text" value={questionSecurite}
                        onChange={(e) => setQuestionSecurite(e.target.value)}
                        required minLength={5}
                        placeholder="Ex : Surnom de mon meilleur ami du primaire ?"
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      />
                    </div>
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Réponse *</label>
                      <input
                        type="text" value={reponseSecurite}
                        onChange={(e) => setReponseSecurite(e.target.value)}
                        required minLength={2}
                        placeholder="Votre réponse confidentielle"
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      />
                      <div className="text-[10px] text-gray-500 mt-1">
                        La casse et les espaces ne comptent pas. Mémorisez-la précieusement.
                      </div>
                    </div>
                  </div>
                </>
              )}

              {error && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">{error}</div>
              )}

              <button
                type="submit" disabled={loading}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 rounded-lg disabled:opacity-50"
              >
                {loading ? 'Enregistrement...' : 'Valider et se connecter'}
              </button>
            </form>
          </>
        )}

        <Link
          to="/portail/login"
          className="block text-center text-sm text-blue-600 dark:text-blue-400 hover:underline mt-4"
        >
          ← Retour à la connexion
        </Link>
      </div>
    </PortailLayout>
  );
}
